import type { CreditLedgerRow } from '@/features/settings/billing/credits/lib/credits'
import {
  type ApiBillingView,
  type CreditSpendStatsView,
  fromProtoSettings,
  mapLedgerEntry,
  mapSpendStats,
  toApiBillingView,
} from '@/features/settings/billing/credits/lib/mappers'
import { orderXClient } from '@/service/client'
import type {
  CreateCreditCheckoutSessionRequest,
  GetCreditSpendStatsRequest,
  ListCreditLedgerRequest,
  ServerMessage,
  UpdateApiBillingSettingsRequest,
} from '@/types/generated/public_api'
import type { ErrorResponse } from '@/types/service'

// Data API credits (ENG-1094). Every request is scoped to the session; none takes a user id.
// A `success=false` answer rejects with its error_message as-is: the server writes those to
// be shown (a credit amount out of range, the step-up prefixes, the settings conflict).

async function post<K extends keyof ServerMessage>(
  request: Record<string, unknown>,
  responseKey: K
): Promise<NonNullable<ServerMessage[K]>> {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', request)
    if (data.error) return Promise.reject(data.error)
    const response = data[responseKey] as
      | (NonNullable<ServerMessage[K]> & { success?: boolean; errorMessage?: string })
      | undefined
    if (!response) return Promise.reject({ status: 500, message: 'Malformed billing response' })
    if (response.success === false) {
      return Promise.reject({ status: 400, message: response.errorMessage ?? '' })
    }
    return response
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

/** Everything the billing page shows: balance, weekly usage, settings, saved card, credit price. */
export async function fetchApiBilling(): Promise<ApiBillingView> {
  return toApiBillingView(await post({ getApiBilling: {} }, 'getApiBillingResponse'))
}

/** Replaces the settings whole; resolves with what the server now stores. */
export async function updateApiBillingSettings(payload: UpdateApiBillingSettingsRequest) {
  const response = await post(
    { updateApiBillingSettings: payload },
    'updateApiBillingSettingsResponse'
  )
  // Defaulting a missing field would write "no limit, refill off" over the cached settings.
  if (!response.settings) {
    return Promise.reject({ status: 500, message: 'Malformed settings response' })
  }
  return fromProtoSettings(response.settings)
}

/** The page is about to navigate to it: never a `javascript:` or other non-web URL. */
function isWebUrl(url: string) {
  try {
    const { protocol } = new URL(url)
    return protocol === 'https:' || protocol === 'http:'
  } catch {
    return false
  }
}

/** Hosted Stripe Checkout for a quantity of credits. Credits land later, via Stripe's webhook. */
export async function createCreditCheckout(
  payload: CreateCreditCheckoutSessionRequest
): Promise<string> {
  const response = await post(
    { createCreditCheckoutSession: payload },
    'createCreditCheckoutSessionResponse'
  )
  if (!response.checkoutUrl || !isWebUrl(response.checkoutUrl)) {
    return Promise.reject({ status: 500, message: 'Malformed checkout response' })
  }
  return response.checkoutUrl
}

export async function listCreditLedger(
  payload: ListCreditLedgerRequest
): Promise<{ entries: CreditLedgerRow[]; nextCursor?: string }> {
  const response = await post({ listCreditLedger: payload }, 'listCreditLedgerResponse')
  return { entries: response.entries.map(mapLedgerEntry), nextCursor: response.nextCursor }
}

export async function fetchCreditSpendStats(
  payload: GetCreditSpendStatsRequest
): Promise<CreditSpendStatsView> {
  return mapSpendStats(await post({ getCreditSpendStats: payload }, 'getCreditSpendStatsResponse'))
}
